import type { MouseEvent } from 'react';
import { useCallback } from 'react';

import Button from '@/components/Button/Button';
import useModalState from '@/hooks/pure/useModalState';
import { cn } from '@/lib/cn';
import { getChartColorByHash } from '@/styles/colors/chartColors';

import { getLegendResetUiState, type IssueLegendMode } from '../../lib/legend-reset-state';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '../../model';
import { IssueActionDropdown } from '../chart.issue-action-dropdown';

export const IssueLegend = (props: {
  data: CompositedSignalIssue[];
  dynamicFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
  onToggle?: (issueName: string) => void;
  hiddenIssueNames?: Set<string>;
  onIsolate?: (issueName: string) => void;
  onShowAll?: () => void;
  mode?: IssueLegendMode;
  isResetEnabled?: boolean;
}) => {
  const dropdownState = useModalState<CompositedSignalIssue | null>(null);
  const { data } = props;
  const hiddenCount = props.hiddenIssueNames?.size ?? 0;
  const resetUiState = getLegendResetUiState({
    hiddenCount,
    isResetEnabled: props.isResetEnabled,
    mode: props.mode,
  });

  // visible 아이템이 1개일 때 마지막 시리즈 보호
  const visibleCount = data.filter(
    (issue) => !(props.hiddenIssueNames?.has(issue.name) ?? false),
  ).length;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[11px] text-gray-500">
        Click to open actions
      </p>
      <div className="flex gap-0.5 flex-wrap items-center">
        {data.map((issue) => {
          const isHidden = props.hiddenIssueNames?.has(issue.name) ?? false;
          return (
            <IssueLegendItem
              key={issue.name}
              issueIndex={issue.issueIndex}
              issueName={issue.name}
              isHidden={isHidden}
              onOpenMenu={(e) => {
                const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                dropdownState.open({
                  modalState: issue,
                  pos: {
                    top: rect.bottom,
                    left: rect.left,
                  },
                });
              }}
            />
          );
        })}
        {resetUiState.shouldShow && (
          <button
            type="button"
            onClick={props.onShowAll}
            disabled={resetUiState.disabled}
            className="text-xs text-gray-500 underline hover:text-gray-700 disabled:no-underline disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {resetUiState.label}
          </button>
        )}
      </div>
      <IssueActionDropdown
        selectedIssue={dropdownState.modalState}
        dynamicFilterSet={props.dynamicFilterSet}
        datePreset={props.datePreset}
        dropdownPosition={dropdownState.pos}
        isDropdownOpen={dropdownState.status}
        closeDropdown={dropdownState.close}
        onToggleSeries={props.onToggle}
        onIsolateSeries={props.onIsolate}
        isSeriesHidden={
          dropdownState.modalState
            ? (props.hiddenIssueNames?.has(dropdownState.modalState.name) ?? false)
            : false
        }
        isLastVisibleSeries={
          visibleCount === 1 &&
          dropdownState.modalState != null &&
          !(props.hiddenIssueNames?.has(dropdownState.modalState.name) ?? false)
        }
      />
    </div>
  );
};

const IssueLegendItem = (props: {
  issueIndex?: number;
  issueName: string;
  isHidden?: boolean;
  onOpenMenu?: (e: MouseEvent) => void;
}) => {
  const { issueIndex, issueName, onOpenMenu } = props;

  const handleClick = useCallback(
    (e: MouseEvent) => {
      onOpenMenu?.(e);
    },
    [onOpenMenu],
  );

  return (
    <Button
      variant="ghost"
      onClick={handleClick}
    >
      <div className="flex items-center gap-1.5">
        <div
          className={cn('size-3 rounded-full', props.isHidden && 'opacity-30')}
          style={{
            backgroundColor: getChartColorByHash(issueName),
          }}
        />
        <span className={cn('text-xs text-gray-700', props.isHidden && 'line-through opacity-50')}>
          {issueIndex}. {issueName}
        </span>
      </div>
    </Button>
  );

};
